<script>
import i18n from "@/i18n";
import { required, minValue, integer } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { salesTargetMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default {
  name: "SalesTargetFormPanel",
  components: { Multiselect },
  props: {
    mode: { type: String, default: "create" }, // 'create' | 'edit'
    agentCode: { type: String, required: true },
    target: { type: Object, default: null },
    existingPlans: { type: Array, default: () => [] },
  },
  data() {
    const now = new Date();
    return {
      form: {
        name: "",
        area_codes: [],
        period_type: "monthly",
        doc_types: [],
        exclude_cancelled: false,
        start_year: now.getFullYear(),
        start_index: now.getMonth() + 1,
        period_count: 12,
        target_amount: 0,
      },
      detailRows: [],
      submitting: false,
      selectedPeriodType: null,
      selectedDocTypes: [],
      selectedAreaCodes: [],
      selectedStartIndex: null,
    };
  },
  validations: {
    form: {
      name: { required },
      period_type: { required },
      start_year: { required, integer, minValue: minValue(2020) },
      start_index: { required, integer, minValue: minValue(1) },
      period_count: { required, integer, minValue: minValue(1) },
      target_amount: { required, minValue: minValue(0.01) },
      doc_types: { required, minLength: (v) => Array.isArray(v) && v.length > 0 },
    },
  },
  computed: {
    ...mapState(LOOKUP_MODULE, ["salesDocTypeDdl", "areaCodeDdl"]),
    isEdit() { return this.mode === "edit"; },
    periodTypeOptions() {
      return [
        { value: "monthly", text: i18n.t("label.monthly") },
        { value: "quarterly", text: i18n.t("label.quarterly") },
        { value: "yearly", text: i18n.t("label.yearly") },
      ];
    },
    startIndexOptions() {
      if (this.form.period_type === "monthly") {
        return MONTH_NAMES.map((m, i) => ({ value: i + 1, text: m }));
      }
      if (this.form.period_type === "quarterly") {
        return [
          { value: 1, text: "Q1 (Jan – Mar)" },
          { value: 2, text: "Q2 (Apr – Jun)" },
          { value: 3, text: "Q3 (Jul – Sep)" },
          { value: 4, text: "Q4 (Oct – Dec)" },
        ];
      }
      return [{ value: 1, text: "—" }];
    },
    startIndexLabel() {
      if (this.form.period_type === "monthly") return i18n.t("salesTarget.form.startMonth");
      if (this.form.period_type === "quarterly") return i18n.t("salesTarget.form.startQuarter");
      return null;
    },
    periodCountLabel() {
      if (this.form.period_type === "monthly") return i18n.t("salesTarget.form.numberOfMonths");
      if (this.form.period_type === "quarterly") return i18n.t("salesTarget.form.numberOfQuarters");
      return i18n.t("salesTarget.form.numberOfYears");
    },
    generatedPeriods() {
      const list = [];
      const type = this.form.period_type;
      const year = Number(this.form.start_year) || new Date().getFullYear();
      const idx = Number(this.form.start_index) || 1;
      const count = Math.min(Math.max(Number(this.form.period_count) || 1, 1), 60);

      if (type === "monthly") {
        let m = idx, y = year;
        for (let i = 0; i < count; i++) {
          const start = new Date(y, m - 1, 1);
          const end = new Date(y, m, 0);
          list.push({ start: this.fmtDate(start), end: this.fmtDate(end), label: `${MONTH_NAMES[m - 1]} ${y}` });
          m++; if (m > 12) { m = 1; y++; }
        }
      } else if (type === "quarterly") {
        let q = idx, y = year;
        for (let i = 0; i < count; i++) {
          const startMonth = (q - 1) * 3 + 1;
          const start = new Date(y, startMonth - 1, 1);
          const end = new Date(y, startMonth + 2, 0);
          list.push({ start: this.fmtDate(start), end: this.fmtDate(end), label: `Q${q} ${y}` });
          q++; if (q > 4) { q = 1; y++; }
        }
      } else {
        for (let i = 0; i < count; i++) {
          const y = year + i;
          list.push({ start: `${y}-01-01`, end: `${y}-12-31`, label: `${y}` });
        }
      }
      return list;
    },
    totalTarget() {
      const per = Number(this.form.target_amount) || 0;
      return per * this.generatedPeriods.length;
    },
    existingTotal() {
      return (this.existingPlans || []).reduce((s, p) => s + Number(p.total_target || 0), 0);
    },
    existingPlansCount() {
      return (this.existingPlans || []).length;
    },
    existingPeriodsCount() {
      return (this.existingPlans || []).reduce((s, p) => s + Number(p.periods_count || 0), 0);
    },
    combinedTotal() {
      return this.existingTotal + this.totalTarget;
    },
    detailsTotal() {
      return this.detailRows.reduce((s, d) => s + (Number(d.target_amount) || 0), 0);
    },
    fmtCurrency() {
      return (v) => {
        const n = Number(v) || 0;
        return n.toLocaleString(undefined, { style: "currency", currency: "MYR", minimumFractionDigits: 2 });
      };
    },
  },
  async created() {
    await Promise.all([this.lookupSalesDocType(), this.lookupAreaCode()]);
    this.hydrateFromTarget();
  },
  watch: {
    target() { this.hydrateFromTarget(); },
  },
  methods: {
    ...salesTargetMethods,
    ...lookupMethods,
    fmtDate(d) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    hydrateFromTarget() {
      if (this.isEdit && this.target) {
        const t = this.target;
        this.form.name = t.name || "";
        this.form.area_codes = Array.isArray(t.area_codes) ? t.area_codes : [];
        this.form.period_type = t.period_type || "monthly";
        this.form.doc_types = Array.isArray(t.doc_types) ? t.doc_types : [];
        this.form.exclude_cancelled = !!t.exclude_cancelled;

        this.detailRows = (t.details || []).map((x) => ({
          id: x.id,
          period_start: x.period_start,
          period_end: x.period_end,
          target_amount: x.target_amount,
          sequence: x.sequence,
        }));
      }
      this.selectedPeriodType = this.periodTypeOptions.find((p) => p.value === this.form.period_type) || null;
      this.selectedDocTypes = (this.salesDocTypeDdl || []).filter((t) => this.form.doc_types.includes(t.value));
      this.selectedAreaCodes = (this.areaCodeDdl || []).filter((a) => this.form.area_codes.includes(a.value));
      this.selectedStartIndex = this.startIndexOptions.find((o) => o.value === this.form.start_index) || this.startIndexOptions[0];
    },
    onPeriodTypeChange(p) {
      this.selectedPeriodType = p;
      this.form.period_type = p ? p.value : "";
      this.form.start_index = 1;
      this.selectedStartIndex = this.startIndexOptions[0];
      this.form.period_count = this.form.period_type === "monthly" ? 12 : this.form.period_type === "quarterly" ? 4 : 1;
    },
    onStartIndexChange(o) {
      this.selectedStartIndex = o;
      this.form.start_index = o ? o.value : 1;
    },
    onDocTypesChange(list) {
      this.selectedDocTypes = list || [];
      this.form.doc_types = this.selectedDocTypes.map((t) => t.value);
    },
    onAreaCodesChange(list) {
      this.selectedAreaCodes = list || [];
      this.form.area_codes = this.selectedAreaCodes.map((a) => a.value);
    },
    async submit() {
      this.$v.form.$touch();
      if (!this.isEdit && this.$v.form.$invalid) return;
      if (this.isEdit && (!this.form.name || !this.form.period_type)) return;

      this.submitting = true;
      try {
        let payload;
        if (this.isEdit) {
          payload = {
            name: this.form.name,
            area_codes: this.form.area_codes,
            doc_types: this.form.doc_types,
            exclude_cancelled: this.form.exclude_cancelled,
            details: this.detailRows.map((d) => ({ id: d.id, target_amount: d.target_amount })),
          };
        } else {
          payload = {
            name: this.form.name,
            agent_code: this.agentCode,
            area_codes: this.form.area_codes,
            period_type: this.form.period_type,
            doc_types: this.form.doc_types,
            exclude_cancelled: this.form.exclude_cancelled,
            start_year: this.form.start_year,
            start_index: this.form.start_index,
            period_count: this.form.period_count,
            target_amount: this.form.target_amount,
          };
        }

        let result;
        if (this.isEdit) {
          result = await this.updateSalesTarget({ id: this.target.id, data: payload, filter: null });
        } else {
          result = await this.createSalesTarget({ data: payload, filter: null });
        }
        if (result) this.$emit("saved");
      } finally {
        this.submitting = false;
      }
    },
    cancel() { this.$emit("cancel"); },
  },
};
</script>

<template>
  <div>
    <!-- PREVIEW at top (create mode only) -->
    <div v-if="!isEdit && generatedPeriods.length" class="preview-banner mb-3">
      <div class="preview-head">
        <i class="mdi mdi-target"></i>
        <span>{{ $t('salesTarget.form.preview') }}</span>
      </div>
      <div class="preview-row-group">
        <div class="preview-row">
          <div class="pv-label">{{ $t('salesTarget.form.existing') }}</div>
          <div class="pv-value">{{ fmtCurrency(existingTotal) }}</div>
          <div class="pv-sub">{{ $t('salesTarget.agentDetail.planCount', [existingPlansCount]) }} · {{ existingPeriodsCount }} {{ $t('salesTarget.agentDetail.periods') }}</div>
        </div>
        <div class="preview-divider">+</div>
        <div class="preview-row">
          <div class="pv-label">{{ $t('salesTarget.form.new') }}</div>
          <div class="pv-value">{{ fmtCurrency(totalTarget) }}</div>
          <div class="pv-sub">{{ generatedPeriods.length }} {{ $t('salesTarget.agentDetail.periods') }}</div>
        </div>
        <div class="preview-divider">=</div>
        <div class="preview-row combined">
          <div class="pv-label">{{ $t('salesTarget.form.combined') }}</div>
          <div class="pv-value">{{ fmtCurrency(combinedTotal) }}</div>
          <div class="pv-sub">{{ $t('salesTarget.form.totalTarget') }}</div>
        </div>
      </div>

      <div class="preview-periods">
        <div class="periods-title">{{ $t('salesTarget.form.newPeriods') }}</div>
        <div class="periods-list">
          <div v-for="(p, i) in generatedPeriods.slice(0, 12)" :key="i" class="period-chip">
            <span class="pc-label">{{ p.label }}</span>
            <span class="pc-range">{{ p.start }} → {{ p.end }}</span>
          </div>
          <div v-if="generatedPeriods.length > 12" class="period-chip more">
            {{ $t('salesTarget.form.more', [generatedPeriods.length - 12]) }}
          </div>
        </div>
      </div>
    </div>

    <div class="row">
    <div class="col-12">
      <b-form @submit.prevent="submit">
        <b-row>
          <b-col cols="12">
            <b-form-group :label="$t('label.name')">
              <b-form-input
                v-model="form.name"
                :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
              />
            </b-form-group>
          </b-col>

          <b-col cols="12">
            <b-form-group :label="$t('salesTarget.form.areaCodes')">
              <multiselect
                :value="selectedAreaCodes"
                :options="areaCodeDdl"
                track-by="value"
                label="name"
                :multiple="true"
                :close-on-select="false"
                :show-labels="false"
                :placeholder="$t('label.select')"
                @input="onAreaCodesChange"
              />
              <small class="text-muted">{{ $t('salesTarget.form.areaCodesHelp') }}</small>
            </b-form-group>
          </b-col>

          <b-col cols="12" v-if="!isEdit">
            <div class="period-card">
              <div class="period-card-header">
                <i class="mdi mdi-calendar-range"></i>
                <span>{{ $t('salesTarget.form.periodSetup') }}</span>
              </div>
              <b-row>
                <b-col cols="12">
                  <b-form-group :label="$t('label.periodType')" class="mb-2">
                    <multiselect
                      :value="selectedPeriodType"
                      :options="periodTypeOptions"
                      track-by="value"
                      label="text"
                      :show-labels="false"
                      :allow-empty="false"
                      :searchable="false"
                      :placeholder="$t('label.select')"
                      @input="onPeriodTypeChange"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="$t('label.year')" class="mb-2">
                    <b-form-input
                      type="number"
                      min="2020"
                      max="2100"
                      v-model.number="form.start_year"
                      :state="$v.form.start_year.$dirty ? !$v.form.start_year.$error : null"
                    />
                    <b-form-invalid-feedback v-if="$v.form.start_year.$dirty && !$v.form.start_year.minValue">
                      {{ $t('salesTarget.form.minYear') }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
                <b-col cols="12" v-if="startIndexLabel">
                  <b-form-group :label="startIndexLabel" class="mb-2">
                    <multiselect
                      :value="selectedStartIndex"
                      :options="startIndexOptions"
                      track-by="value"
                      label="text"
                      :show-labels="false"
                      :allow-empty="false"
                      :searchable="false"
                      :placeholder="$t('label.select')"
                      @input="onStartIndexChange"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="12">
                  <b-form-group :label="periodCountLabel" class="mb-0">
                    <b-form-input
                      type="number"
                      min="1"
                      max="60"
                      v-model.number="form.period_count"
                      :state="$v.form.period_count.$dirty ? !$v.form.period_count.$error : null"
                    />
                    <b-form-invalid-feedback v-if="$v.form.period_count.$dirty && !$v.form.period_count.minValue">
                      {{ $t('salesTarget.form.minPeriodCount') }}
                    </b-form-invalid-feedback>
                    <small class="text-muted">{{ $t('salesTarget.form.willCreate', [form.period_count]) }}</small>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </b-col>

          <b-col cols="12" v-if="!isEdit">
            <b-form-group :label="$t('salesTarget.form.targetAmountPer')">
              <b-input-group prepend="RM">
                <b-form-input
                  type="number"
                  step="0.01"
                  v-model.number="form.target_amount"
                  :state="$v.form.target_amount.$dirty ? !$v.form.target_amount.$error : null"
                />
              </b-input-group>
              <b-form-invalid-feedback :state="$v.form.target_amount.$dirty ? !$v.form.target_amount.$error : null" v-if="$v.form.target_amount.$dirty && !$v.form.target_amount.minValue">
                {{ $t('salesTarget.form.minTarget') }}
              </b-form-invalid-feedback>
              <small class="text-muted">{{ $t('salesTarget.form.targetAmountHelp') }}</small>
            </b-form-group>
          </b-col>

          <b-col cols="12" v-if="isEdit">
            <div class="period-card">
              <div class="period-card-header">
                <i class="mdi mdi-format-list-numbered"></i>
                <span>{{ $t('salesTarget.form.periodCount', [detailRows.length]) }}</span>
              </div>
              <div class="detail-row" v-for="(d, idx) in detailRows" :key="d.id">
                <div class="detail-label">
                  <span class="seq">#{{ idx + 1 }}</span>
                  {{ d.period_start }} → {{ d.period_end }}
                </div>
                <b-input-group prepend="RM" class="detail-amount">
                  <b-form-input type="number" step="0.01" v-model.number="d.target_amount" />
                </b-input-group>
              </div>
              <div class="detail-total">
                <span>Total</span>
                <strong>{{ fmtCurrency(detailsTotal) }}</strong>
              </div>
            </div>
          </b-col>

          <b-col cols="12">
            <div class="rules-card">
              <div class="rules-card-header">
                <i class="mdi mdi-tune-variant"></i>
                <span>{{ $t('salesTarget.form.calcRules') }}</span>
              </div>
              <b-form-group :label="$t('salesTarget.form.documentTypesRequired')" class="mb-2">
                <div :class="$v.form.doc_types.$dirty && $v.form.doc_types.$error ? 'multiselect-invalid' : ''">
                  <multiselect
                    :value="selectedDocTypes"
                    :options="salesDocTypeDdl"
                    track-by="value"
                    label="name"
                    :multiple="true"
                    :close-on-select="false"
                    :show-labels="false"
                    :placeholder="$t('label.select')"
                    @input="onDocTypesChange"
                  />
                </div>
                <small v-if="$v.form.doc_types.$dirty && $v.form.doc_types.$error" class="text-danger">
                  {{ $t('salesTarget.form.documentTypesError') }}
                </small>
                <small v-else class="text-muted">{{ $t('salesTarget.form.documentTypesHelp') }}</small>
              </b-form-group>
              <b-form-group class="mb-0">
                <b-form-checkbox v-model="form.exclude_cancelled" switch>
                  {{ $t('salesTarget.form.excludeCancelled') }}
                </b-form-checkbox>
              </b-form-group>
            </div>
          </b-col>
        </b-row>
        <div class="text-right">
          <b-button variant="light" class="mr-2" @click="cancel">{{ $t('label.cancel') }}</b-button>
          <b-button type="submit" variant="primary" :disabled="submitting">{{ $t('label.submit') }}</b-button>
        </div>
      </b-form>
    </div>
    </div>
  </div>
</template>

<style scoped>
.period-card, .rules-card {
  border: 1px solid rgba(124, 77, 255, 0.18);
  background: rgba(124, 77, 255, 0.04);
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 16px;
}
.period-card-header, .rules-card-header {
  font-size: 13px; font-weight: 600; color: #7C4DFF;
  letter-spacing: 0.3px; text-transform: uppercase;
  margin-bottom: 12px; display: flex; align-items: center; gap: 6px;
}
.period-card-header i, .rules-card-header i { font-size: 18px; }
.detail-row {
  display: flex; align-items: center; gap: 12px;
  padding: 8px 0; border-bottom: 1px solid rgba(124, 77, 255, 0.1);
}
.detail-row:last-of-type { border-bottom: none; }
.detail-label { flex: 1; font-size: 13px; color: #495057; }
.detail-label .seq {
  display: inline-block; background: rgba(124, 77, 255, 0.15);
  color: #7C4DFF; font-weight: 600; padding: 2px 6px;
  border-radius: 4px; font-size: 11px; margin-right: 8px;
}
.detail-amount { width: 180px; }
.detail-total {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 8px; padding-top: 8px;
  border-top: 2px solid rgba(124, 77, 255, 0.18);
  font-size: 13px; color: #7C4DFF;
}
.preview-banner {
  background: linear-gradient(135deg, #5E2FE0 0%, #7C4DFF 55%, #9C6AFF 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 10px 24px rgba(124, 77, 255, 0.28);
}
.preview-head {
  font-size: 13px; font-weight: 600; letter-spacing: 0.4px;
  text-transform: uppercase; opacity: 0.9; margin-bottom: 14px;
  display: flex; align-items: center; gap: 6px;
}
.preview-head i { font-size: 18px; }
.preview-row-group {
  display: flex;
  align-items: stretch;
  gap: 14px;
  margin-bottom: 16px;
}
.preview-row {
  flex: 1;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}
.preview-row.combined {
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.preview-divider {
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 300; opacity: 0.6;
  min-width: 12px;
}
.pv-label {
  font-size: 11px; letter-spacing: 0.4px; text-transform: uppercase; opacity: 0.85;
}
.pv-value {
  font-size: 20px; font-weight: 700; letter-spacing: -0.3px; margin-top: 4px;
}
.preview-row.combined .pv-value { font-size: 24px; }
.pv-sub { font-size: 10px; opacity: 0.75; margin-top: 3px; }

.preview-periods {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
.periods-title {
  font-size: 11px; opacity: 0.85; margin-bottom: 8px;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.periods-list {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.period-chip {
  background: rgba(255, 255, 255, 0.15);
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  display: flex; flex-direction: column;
  min-width: 110px;
}
.period-chip.more {
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  align-items: center; justify-content: center; min-width: 80px;
  opacity: 0.75;
}
.pc-label { font-weight: 700; }
.pc-range { opacity: 0.75; font-size: 10px; }

@media (max-width: 768px) {
  .preview-row-group { flex-direction: column; }
  .preview-divider { display: none; }
}

.multiselect-invalid ::v-deep .multiselect__tags {
  border-color: #f46a6a;
}
</style>
